import { Link } from "react-router-dom";
import { ArrowLeft, Check, Sparkles, ArrowUpRight } from "lucide-react";

const WHATSAPP_NUMBER = "918097208707";

const plans = [
  {
    id: "basic",
    name: "Basic Plan",
    price: "₹5,000",
    cadence: "/ month",
    badge: null,
    tagline: "Suitable for Small Businesses & Local Leaders",
    features: [
      "1 Facebook Account Management",
      "Daily 2–4 Posts Update",
      "Party Posts Re-Posting",
      "Party Video Re-Posting",
      "Custom Graphic Posts (Events, Festivals, Greetings)",
      "Caption Writing, Tagging & Hashtag Management",
      "Basic Page Optimization",
    ],
  },
  {
    id: "standard",
    name: "Standard Plan",
    price: "₹10,000",
    cadence: "/ month",
    badge: "Most Popular",
    tagline: "Suitable for Political Leaders, Organizations & Growing Brands",
    features: [
      "Management of 3 Social Media Accounts",
      "Facebook, Instagram & X (Twitter)",
      "Daily 4–6 Posts Update",
      "Party Posts Re-Posting",
      "Party Videos Re-Posting",
      "Custom Graphic Posts (Events, Festivals, Campaigns)",
      "Caption Writing, Tagging & Hashtag Strategy",
      "Story Updates (10–15 per Month)",
      "Comment & Message Monitoring",
      "Basic Audience Engagement",
      "Monthly Analytics Report",
      "Profile Optimization on All Platforms",
      "Festival & Campaign Content Planning",
    ],
  },
  {
    id: "premium",
    name: "Premium Plan",
    price: "₹20,000",
    cadence: "/ month",
    badge: "Best Value",
    tagline: "Suitable for Political Campaigns, Public Figures & Businesses",
    features: [
      "Management of Up to 5 Social Media Accounts",
      "Facebook, Instagram, X (Twitter), YouTube & LinkedIn",
      "Daily 6–10 Posts Update",
      "Custom Graphics & Professional Creatives",
      "Video Editing & Reels Creation (8–12 per Month)",
      "Story Updates & Highlights Management",
      "Content Calendar Planning",
      "Caption Writing, Tagging & Hashtag Research",
      "Audience Engagement & Comment Replies",
      "Monthly Analytics & Growth Report",
      "Profile Optimization & Branding",
      "Event Coverage Content Support",
      "Priority Support",
    ],
  },
];

function buildWaLink(planName, price) {
  const msg = `Hi Mahaview Enterprises, I'm interested in the ${planName} (${price}/month). Please share details.`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
}

export default function Pricing() {
  return (
    <main data-testid="pricing-page" className="relative bg-[#050505] text-white min-h-screen mv-grain overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-[#050505]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="pricing-brand" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_digital-presence-hub-12/artifacts/99t6h1ly_mahaview-new-logo.png"
              alt="Mahaview Enterprises"
              className="h-9 md:h-10 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="pricing-back-link"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-[#FF5A00] transition-colors"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-16 pb-10 md:pt-28 md:pb-16 text-center md:text-left">
        <div className="inline-flex items-center gap-2 border border-white/15 bg-white/5 backdrop-blur-md px-3 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.25em] text-zinc-200 mb-6 md:mb-8">
          <Sparkles size={12} className="text-[#FF5A00]" />
          <span>Transparent Pricing</span>
        </div>
        <h1 className="font-display text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight break-words">
          Social Media<br />
          <span className="text-[#FF5A00]">Management Packages</span>
        </h1>
        <p className="text-zinc-400 mt-5 md:mt-6 max-w-2xl text-sm sm:text-base md:text-lg leading-relaxed">
          Choose the package that fits your goals — designed for small businesses, political leaders,
          organisations and growing brands. Custom plans available on request.
        </p>
      </section>

      {/* Pricing cards */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pb-20 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {plans.map((p) => {
            const popular = p.badge === "Most Popular";
            const premium = p.badge === "Best Value";
            const highlight = popular || premium;
            return (
              <article
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`relative flex flex-col p-7 md:p-9 border ${highlight ? "border-[#FF5A00] bg-[#0d0d0d]" : "border-white/10 bg-[#0a0a0a]"} transition-colors`}
              >
                {p.badge && (
                  <div className={`absolute top-0 right-6 -translate-y-1/2 text-white text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 ${premium ? "bg-white text-[#050505]" : "bg-[#FF5A00]"}`}>
                    {p.badge}
                  </div>
                )}
                <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 mb-3">{p.name}</div>
                <div className="flex items-baseline gap-2 mb-3">
                  <div className="font-display text-5xl md:text-6xl font-semibold text-[#FF5A00] tracking-tight">
                    {p.price}
                  </div>
                  <div className="text-sm text-zinc-400">{p.cadence}</div>
                </div>
                <p className="text-sm text-zinc-300 mb-8 leading-relaxed">{p.tagline}</p>

                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Services Included</div>
                <ul className="space-y-3 mb-10">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-200">
                      <span className={`mt-[3px] flex-shrink-0 w-5 h-5 ${highlight ? "bg-[#FF5A00]" : "bg-white/10"} flex items-center justify-center`}>
                        <Check size={12} className="text-white" strokeWidth={3} />
                      </span>
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={buildWaLink(p.name, p.price)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`plan-cta-${p.id}`}
                  className={`inline-flex items-center justify-center gap-2 px-7 py-4 text-sm font-medium rounded-sm transition-colors mt-auto ${
                    highlight
                      ? "mv-btn-primary text-white"
                      : "border border-white/20 hover:border-[#FF5A00] hover:text-[#FF5A00] text-white"
                  }`}
                >
                  Get Started on WhatsApp
                  <ArrowUpRight size={16} />
                </a>
              </article>
            );
          })}
        </div>

        {/* Extra Services */}
        <div className="mt-20 md:mt-28" data-testid="extra-services">
          <div className="grid grid-cols-12 gap-6 mb-10">
            <div className="col-span-12 md:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5A00] mb-4">/ Add-Ons</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
                Extra Services <span className="text-stroke">(Add-On Charges)</span>
              </h2>
              <p className="text-zinc-400 mt-4 max-w-xl text-sm md:text-base">
                Mix and match these à la carte services with any plan above, or buy them standalone.
              </p>
            </div>
          </div>

          <div className="border border-white/10 bg-[#0a0a0a] divide-y divide-white/10">
            {[
              { name: "Reel Editing", price: "₹300 – ₹800 per Reel" },
              { name: "Professional Video Editing", price: "₹1,000 – ₹5,000 per Video" },
              { name: "Social Media Ad Campaign Management", price: "₹6,000 / month + Ad Budget" },
              { name: "Graphic Design (Extra)", price: "₹200 – ₹600 per Design" },
              { name: "YouTube Channel Management", price: "₹3,000 – ₹10,000 / month" },
              { name: "WhatsApp Marketing", price: "₹5,000 / month" },
              { name: "Political Campaign Content Package", price: "Custom Pricing" },
            ].map((row, i) => (
              <div
                key={row.name}
                data-testid={`extra-row-${i}`}
                className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-6 px-5 md:px-8 py-5 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-start gap-4 min-w-0">
                  <span className="text-[10px] tracking-[0.3em] text-zinc-500 mt-1 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="font-display text-lg md:text-xl text-white tracking-tight">
                    {row.name}
                  </div>
                </div>
                <div className="text-[#FF5A00] font-medium text-sm md:text-base whitespace-nowrap pl-9 md:pl-0">
                  {row.price}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footnote / custom plan strip */}
        <div className="mt-12 md:mt-16 border border-white/10 bg-[#0a0a0a] p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5A00] mb-2">Need Something Bigger?</div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
              Custom enterprise & political campaign packages available.
            </h3>
            <p className="text-zinc-400 mt-2 text-sm">
              Live streaming · multi-platform broadcasts · graphic + video production · web development.
            </p>
          </div>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Mahaview, I need a custom package. Please share details.")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="custom-plan-cta"
            className="mv-btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium rounded-sm whitespace-nowrap"
          >
            Talk to Us
            <ArrowUpRight size={16} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10 px-6 md:px-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-zinc-400">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-[#FF5A00]" />
            <span>© {new Date().getFullYear()} Mahaview Enterprises · All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-[#FF5A00] transition-colors">Home</Link>
            <Link to="/privacy" className="hover:text-[#FF5A00] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#FF5A00] transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
